import { useEffect, useMemo, useState } from 'react';
import { RecipeCollectionClient } from './recipeCollectionClient';
import { useCollectionStore } from '@/stores/collectionStore';
import type { RecipeCollection, UpdateCollectionRequest } from '@/types/api';

export interface CollectionQueryParams {
  /** Filter collections by title (case-insensitive substring match). */
  search?: string;
  /** Return only the first N collections. */
  limit?: number;
}

/**
 * Data management layer for recipe collections.
 *
 * - Reads:    useCollections() provides a reactive subscription for components.
 *             ensureLoaded() triggers the store fetch if not yet loaded.
 *             findById() returns a one-shot value from the current store state.
 * - Writes:   create/update/delete call the API and keep the store in sync.
 *
 * Complex mutations with optimistic updates or background side-effects
 * (addRecipes, removeRecipe, updateIngredient, reorderIngredients,
 * generateIngredients) remain in useRecipeCollectionActions where the
 * rollback logic lives.
 */
export const CollectionService = {
  /**
   * Fire-and-forget: triggers a full fetch if the store hasn't loaded yet.
   * Safe to call from useEffect — idempotent if already loading or loaded.
   */
  ensureLoaded(): void {
    const { status } = useCollectionStore.getState();
    if (status === 'idle') void CollectionService.load();
  },

  async load(): Promise<void> {
    const store = useCollectionStore.getState();
    if (store.status === 'loading') return;
    store.setStatus('loading');
    try {
      const collections = await RecipeCollectionClient.getCollections();
      // Merge — preserve richer detail data that the list endpoint doesn't return
      const existing = useCollectionStore.getState().collections;
      const merged = collections.map((incoming) => {
        const prev = existing.find((c) => c.id === incoming.id);
        if (!prev) return incoming;
        return {
          ...prev,
          ...incoming,
          recipeIds: incoming.recipeIds ?? prev.recipeIds,
          ingredients: incoming.ingredients ?? prev.ingredients,
        };
      });
      store.setCollections(merged);
      store.setStatus('loaded');
    } catch {
      store.setStatus('error');
    }
  },

  async loadOne(id: string): Promise<void> {
    const collection = await RecipeCollectionClient.getCollection(id);
    if (!collection) throw new Error('Collection not found');
    useCollectionStore.getState().upsert(collection);
  },

  upsert(collection: RecipeCollection): void {
    useCollectionStore.getState().upsert(collection);
  },

  findById(id: string): RecipeCollection | undefined {
    return useCollectionStore.getState().collections.find((c) => c.id === id);
  },

  clear(): void {
    useCollectionStore.getState().clear();
  },

  /**
   * Clears the store and triggers a full reload from the API.
   * Use for "Refresh app data" / hard-reset scenarios.
   */
  async reset(): Promise<void> {
    CollectionService.clear();
    return CollectionService.load();
  },

  /**
   * React hook: reactive subscription to the collection store.
   *
   * Re-renders callers whenever the store changes. All filter/limit logic is
   * delegated to filter() — this method only owns the React-specific
   * subscription and memoisation.
   *
   * The store is intentionally kept as an internal detail of this file;
   * nothing outside the repository should import from collectionStore directly.
   */
  useCollections(options?: CollectionQueryParams) {
    useEffect(() => {
      CollectionService.ensureLoaded();
    }, []);

    const { collections, status } = useCollectionStore();
    const hasData = collections.length > 0 || status === 'loaded';

    const result = useMemo(() => {
      if (!hasData) return [];
      const search = options?.search?.trim().toLowerCase();
      let output = search
        ? collections.filter((c) => {
            const title = c.title.toLowerCase();
            return search.split(/\s+/).every((word) => title.includes(word));
          })
        : collections;
      if (options?.limit != null) output = output.slice(0, options.limit);
      return output;
    }, [collections, hasData, options?.search, options?.limit]);

    return {
      collections: result,
      isLoading: !hasData && (status === 'loading' || status === 'idle'),
      isRefreshing: hasData && status === 'loading',
      isError: status === 'error',
    };
  },

  /**
   * React hook: reactive subscription to a single collection by ID.
   *
   * If the collection is in the store but lacks ingredient detail (ingredients === undefined),
   * fetches the full version from the detail endpoint automatically.
   * Returns { collection, isLoading, isError }.
   */
  useCollectionById(id: string | undefined) {
    const [fetching, setFetching] = useState(false);
    const [fetchError, setFetchError] = useState(false);

    const collection = useCollectionStore((state) =>
      id ? state.collections.find((c) => c.id === id) : undefined,
    );
    const globalStatus = useCollectionStore((state) => state.status);

    useEffect(() => {
      if (!id) return;
      if (!collection || collection.ingredients === undefined) {
        setFetching(true);
        CollectionService.loadOne(id)
          .catch(() => setFetchError(true))
          .finally(() => setFetching(false));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    return {
      collection,
      isLoading: !collection && !fetchError && (fetching || globalStatus === 'loading' || globalStatus === 'idle'),
      isError: fetchError,
    };
  },

  // ---- Mutations ----

  async create(title: string): Promise<RecipeCollection> {
    const collection = await RecipeCollectionClient.createCollection(title);
    useCollectionStore.getState().upsert(collection);
    return collection;
  },

  async update(id: string, data: UpdateCollectionRequest): Promise<RecipeCollection> {
    const collection = await RecipeCollectionClient.updateCollection(id, data);
    useCollectionStore.getState().upsert(collection);
    return collection;
  },

  async delete(id: string): Promise<void> {
    await RecipeCollectionClient.deleteCollection(id);
    useCollectionStore.getState().remove(id);
  },

  async addRecipes(groupId: string, recipeIds: string[]): Promise<RecipeCollection> {
    const collection = await RecipeCollectionClient.addRecipesToCollection(groupId, recipeIds);
    useCollectionStore.getState().upsert(collection);
    return collection;
  },

  async removeRecipe(groupId: string, recipeId: string): Promise<void> {
    await RecipeCollectionClient.removeRecipeFromCollection(groupId, recipeId);
    // API returns void — filter the recipe ID out of the store optimistically.
    // The subsequent generateIngredients call will overwrite with server state.
    const current = useCollectionStore.getState().collections.find((c) => c.id === groupId);
    if (current) {
      useCollectionStore.getState().upsert({
        ...current,
        recipeIds: current.recipeIds?.filter((id) => id !== recipeId) ?? [],
      });
    }
  },

  async updateIngredientStatus(
    groupId: string,
    ingredientId: string,
    status: 'have' | 'dont-have'
  ): Promise<RecipeCollection> {
    const collection = await RecipeCollectionClient.updateIngredientStatusById(groupId, ingredientId, { status });
    useCollectionStore.getState().upsert(collection);
    return collection;
  },

  async reorderIngredients(
    groupId: string,
    updates: { id: string; index: number }[]
  ): Promise<RecipeCollection> {
    const collection = await RecipeCollectionClient.reorderIngredients(groupId, { ingredientUpdates: updates });
    useCollectionStore.getState().upsert(collection);
    return collection;
  },

  async generateIngredients(groupId: string): Promise<RecipeCollection> {
    const collection = await RecipeCollectionClient.generateIngredients(groupId);
    useCollectionStore.getState().upsert(collection);
    return collection;
  },
};

/**
 * Convenience named export so components can write:
 *   import { useAllCollections } from '@/lib/collectionService'
 * rather than importing CollectionService and calling the method.
 */
export function useAllCollections(options?: CollectionQueryParams) {
  return CollectionService.useCollections(options);
}

/**
 * Convenience named export for a reactive single-collection subscription.
 *   import { useCollectionById } from '@/lib/collectionService'
 */
export function useCollectionById(id: string | undefined) {
  return CollectionService.useCollectionById(id);
}
